import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { motion } from "framer-motion";
import { AlertTriangle, CheckCircle, Database, Mail, Wifi, RefreshCw } from "lucide-react";
import { cn } from "@/lib/utils";
import { TechnologyStatus } from "@/types/technology-status";
import { Button } from "@/components/ui/button";

const TooltipProvider = TooltipPrimitive.Provider;
const Tooltip = TooltipPrimitive.Root;
const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> & {
    showArrow?: boolean;
  }
>(({ className, sideOffset = 4, showArrow = false, ...props }, ref) => (
  <TooltipPrimitive.Portal>
    <TooltipPrimitive.Content
      ref={ref}
      sideOffset={sideOffset}
      className={cn(
        "relative z-50 max-w-[280px] rounded-lg border border-border bg-popover px-3 py-1.5 text-sm text-popover-foreground animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
        className
      )}
      {...props}
    >
      {props.children}
      {showArrow && (
        <TooltipPrimitive.Arrow className="-my-px fill-popover drop-shadow-[0_1px_0_hsl(var(--border))]" />
      )}
    </TooltipPrimitive.Content>
  </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

interface HUDStatusProps {
  status: TechnologyStatus;
  className?: string;
  includeRealtime?: boolean;
  onRefresh?: () => void;
  isRefreshing?: boolean;
}

export const HUDStatus: React.FC<HUDStatusProps> = ({ status, className, includeRealtime = false, onRefresh, isRefreshing }) => {
  const isOperational = status.overall.operational;
  
  // Determine the specific issue if not operational
  const getStatusText = () => {
    if (status.overall.message === 'Checking systems...') {
      return "CHECKING SYSTEMS...";
    }
    if (isOperational) {
      return "ALL SYSTEMS OPERATIONAL";
    }
    
    // Check specific issues
    const issues = [];
    if (!status.database.connected) {
issues.push("DATABASE");
}
    if (!status.gmail.connected) {
issues.push("GMAIL");
}
    if (includeRealtime && !status.realtime.connected) {
issues.push("REALTIME");
}
    
    if (issues.length === 1) {
      return `${issues[0]} CONNECTION ISSUE`;
    } else if (issues.length > 1) {
      return "SYSTEM ISSUES DETECTED";
    }
    
    return "SYSTEM ERROR";
  };

  const getTooltipContent = () => {
    if (isOperational) {
      return "All systems are running normally. No issues detected.";
    }
    
    const details = [];
    if (!status.database.connected) {
      details.push("Database: Not connected");
    }
    if (!status.gmail.connected) {
      details.push("Gmail: Not connected - Email functionality limited");
    }
    if (includeRealtime && !status.realtime.connected) {
      details.push("Real-time sync: Not connected");
    }
    
    return `${details.join(". ")  }.`;
  };

  const statusConfig = isOperational
    ? {
        text: getStatusText(),
        icon: CheckCircle,
        color: "text-green-400",
        bgColor: "bg-green-500/20",
        borderColor: "border-green-500/50",
        glowColor: "shadow-green-500/25",
        tooltipContent: getTooltipContent()
      }
    : {
        text: getStatusText(),
        icon: AlertTriangle,
        color: "text-red-400",
        bgColor: "bg-red-500/20",
        borderColor: "border-red-500/50",
        glowColor: "shadow-red-500/25",
        tooltipContent: getTooltipContent()
      };

  const StatusIcon = statusConfig.icon;

  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <motion.div
            data-status={isOperational ? 'operational' : 'degraded'}
            className={cn(
              "relative inline-flex items-center gap-3 px-6 py-4 rounded-lg border-2 backdrop-blur-sm",
              "font-mono text-sm font-bold tracking-wider uppercase",
              "transition-all duration-500 cursor-pointer select-none",
              statusConfig.color,
              statusConfig.bgColor,
              statusConfig.borderColor,
              `shadow-lg ${statusConfig.glowColor}`,
              className
            )}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3 }}
            whileHover={{ scale: 1.02 }}
          >
            {/* Animated background grid pattern */}
            <div className="hud-status__pattern absolute inset-0 opacity-10">
              <div className="w-full h-full bg-[radial-gradient(circle_at_1px_1px,_currentColor_1px,_transparent_0)] bg-[length:20px_20px]" />
            </div>

            {/* Status indicator */}
            <motion.div
              className="hud-status__icon relative z-10"
              animate={{
                rotate: !isOperational ? [0, 360] : 0,
                scale: status.overall.message === 'Checking systems...' ? [1, 1.1, 1] : 1
              }}
              transition={{
                rotate: { duration: 2, repeat: !isOperational ? Infinity : 0, ease: "linear" },
                scale: { duration: 1, repeat: status.overall.message === 'Checking systems...' ? Infinity : 0 }
              }}
            >
              <StatusIcon size={20} />
            </motion.div>

            {/* Status text */}
            <motion.span
              key={statusConfig.text}
              className="relative z-10"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              {statusConfig.text}
            </motion.span>

            {/* Pulsing border effect */}
            <motion.div
              className={cn(
                "hud-status__pulse absolute inset-0 rounded-lg border-2 opacity-50",
                statusConfig.borderColor
              )}
              animate={{
                scale: [1, 1.02, 1],
                opacity: [0.5, 0.8, 0.5]
              }}
              transition={{
                duration: 2,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />

            {/* Corner accents */}
            <div className="hud-status__corner absolute top-1 left-1 w-3 h-3 border-l-2 border-t-2 border-current opacity-60" />
            <div className="hud-status__corner absolute top-1 right-1 w-3 h-3 border-r-2 border-t-2 border-current opacity-60" />
            <div className="hud-status__corner absolute bottom-1 left-1 w-3 h-3 border-l-2 border-b-2 border-current opacity-60" />
            <div className="hud-status__corner absolute bottom-1 right-1 w-3 h-3 border-r-2 border-b-2 border-current opacity-60" />
          </motion.div>
        </TooltipTrigger>
        <TooltipContent
          className="bg-background/95 backdrop-blur-sm border-border/50 max-w-xs"
          showArrow={true}
        >
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <StatusIcon size={16} className={statusConfig.color} />
              <span className="font-semibold">System Status</span>
            </div>
            <p className="text-muted-foreground text-xs leading-relaxed">
              {statusConfig.tooltipContent}
            </p>
            
            {/* Detailed breakdown */}
            <div className="pt-2 border-t border-border/50 space-y-1">
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-1">
                  <Database size={12} />
                  <span>Database</span>
                </div>
                <span className={status.database.connected ? "text-green-500" : "text-red-500"}>
                  {status.database.connected ? "Connected" : "Disconnected"}
                </span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-1">
                  <Mail size={12} />
                  <span>Gmail</span>
                </div>
                <span className={status.gmail.connected ? "text-green-500" : "text-red-500"}>
                  {status.gmail.connected ? "Connected" : "Disconnected"}
                </span>
              </div>
              {includeRealtime && (
                <div className="flex items-center justify-between text-xs">
                  <div className="flex items-center gap-1">
                    <Wifi size={12} />
                    <span>Real-time</span>
                  </div>
                  <span className={status.realtime.connected ? "text-green-500" : "text-red-500"}>
                    {status.realtime.connected ? "Connected" : "Disconnected"}
                  </span>
                </div>
              )}
            </div>
            
            {!isOperational && (
              <div className="pt-2 border-t border-border/50">
                <p className="text-xs text-muted-foreground">
                  <span className="font-medium">Last Update:</span> {new Date().toLocaleTimeString()}
                </p>
              </div>
            )}
            
            {/* Refresh button */}
            {onRefresh && (
              <div className="pt-2 border-t border-border/50">
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full"
                  onClick={onRefresh}
                  disabled={isRefreshing}
                >
                  {isRefreshing ? (
                    <>
                      <RefreshCw className="mr-2 h-3 w-3 animate-spin" />
                      Refreshing...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="mr-2 h-3 w-3" />
                      Refresh Status
                    </>
                  )}
                </Button>
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
